/**
 * A generated avatar for an account with no picture (HS2-6434HN): the person's initials on a colour
 * derived from their name, as an SVG data URL for Kerf's `avatarImage`. The same name always gives the
 * same image, and each image is built once and cached.
 */
const cache = new Map<string, string>();

/** Up to two initials: the first letters of the first and last words of `name`. */
export function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/[\s._-]+/)
    .filter((word) => word !== '');
  const first = words[0]?.[0];
  if (first === undefined) return '·';
  const last = words.length > 1 ? words[words.length - 1]?.[0] : undefined;
  return `${first}${last ?? ''}`.toUpperCase();
}

/** A stable hue (0-359) for `name`. */
function hueOf(name: string): number {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) % 360;
  return hash;
}

const escapeXml = (text: string): string =>
  text.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[c] ?? c);

/** The cached SVG data URL showing `name`'s initials. */
export function initialsAvatarUrl(name: string): string {
  const key = name.trim();
  const cached = cache.get(key);
  if (cached !== undefined) return cached;
  const initials = initialsOf(key);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<rect width="64" height="64" fill="hsl(${String(hueOf(key))} 45% 38%)"/>` +
    `<text x="32" y="32" dy=".35em" text-anchor="middle" fill="#fff" ` +
    `font-family="system-ui, -apple-system, Segoe UI, sans-serif" font-size="${initials.length > 1 ? 26 : 30}" ` +
    `font-weight="600">${escapeXml(initials)}</text></svg>`;
  const url = `data:image/svg+xml;base64,${btoa(String.fromCharCode(...new TextEncoder().encode(svg)))}`;
  cache.set(key, url);
  return url;
}

/** The app CSP permits local images and data URLs, but blocks external OIDC picture URLs. */
export function profileAvatar(name: string, picture: string): { url: string; scrim: boolean } {
  const local = picture.startsWith('/') && !picture.startsWith('//');
  const embedded = picture.startsWith('data:image/');
  return local || embedded ? { url: picture, scrim: true } : { url: initialsAvatarUrl(name), scrim: false };
}
